import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  type ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ActionButtonDirective } from '@bizzdesign/shared/directives';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import { type ActionButton, type BDMenuItem } from '@bizzdesign/shared/models';
import { TranslateActionButtonPipe } from '@bizzdesign/shared/pipes';
import { SearchbarComponent } from '@bizzdesign/ui-searchbar';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';

const SCROLL_LOAD_THRESHOLD_PX = 150;
const TOP_ITEMS_SKELETON_COUNT = 3;
// Row budget for top-items + all-items — used to size skeleton fills.
const TOTAL_MENU_ROWS = 9;

@Component({
  selector: 'bd-searchable-menu',
  imports: [
    AsyncPipe,
    NgTemplateOutlet,
    ActionButtonDirective,
    ButtonModule,
    DividerModule,
    SearchbarComponent,
    SharedI18nModule,
    SkeletonModule,
    TooltipModule,
    TranslateActionButtonPipe,
  ],
  templateUrl: './searchable-menu.component.html',
  styleUrl: './searchable-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchableMenuComponent {
  readonly topItems = input<BDMenuItem[]>([]);
  readonly allItems = input<BDMenuItem[]>([]);
  readonly hasMore = input<boolean>(false);
  readonly allItemsPageSize = input<number>(1);
  readonly isTopItemsLoading = input<boolean>(false);
  readonly isAllItemsLoading = input<boolean>(false);
  readonly activeItemId = input<string | undefined>(undefined);
  readonly topItemsSkeletonRows = Array.from(
    { length: TOP_ITEMS_SKELETON_COUNT },
    (_, i) => i,
  );
  readonly footerAction = input<ActionButton | undefined>(undefined);
  readonly searchPlaceholder = input<string>('');
  readonly topItemsTitle = input<string | undefined>(undefined);
  readonly noItemsText = input<string | undefined>(undefined);

  readonly searchChange = output<string>();
  readonly loadMore = output<void>();
  readonly itemClick = output<BDMenuItem>();
  // Fires when a user action (item pick or footer button) should dismiss the
  // popover that hosts this menu. The consumer owns the popover, not us.
  readonly dismiss = output<void>();

  readonly scrollContainer =
    viewChild<ElementRef<HTMLElement>>('scrollContainer');

  readonly searchTerm = signal('');
  readonly previewTooltipsDisabled = signal(false);
  private previewTooltipSuppressionTimer:
    | ReturnType<typeof setTimeout>
    | undefined;

  readonly filteredAllItems = computed<BDMenuItem[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const items = this.allItems();
    if (term) {
      return items.filter((item) =>
        (item.label ?? '').toLowerCase().includes(term),
      );
    }
    const topIds = new Set(
      this.topItems()
        .map((it) => it.id)
        .filter((id): id is string => id !== undefined),
    );
    const activeId = this.activeItemId();
    return items.filter(
      (item) =>
        !(item.id !== undefined && topIds.has(item.id)) && item.id !== activeId,
    );
  });

  readonly showTopItems = computed(
    () => !this.searchTerm().trim() && this.topItems().length > 0,
  );

  readonly showTopItemsLoading = computed(
    () =>
      !this.searchTerm().trim() &&
      this.isTopItemsLoading() &&
      this.topItems().length === 0,
  );

  // Fill the free capacity while loading; on scroll-load use at least
  // pageSize so the incoming batch is visible. Capped at capacity.
  readonly topItemsRowsInView = computed(() => {
    if (this.showTopItems()) return this.topItems().length;
    if (this.showTopItemsLoading()) return this.topItemsSkeletonRows.length;
    return 0;
  });

  readonly allItemsSkeletonRows = computed(() => {
    if (!this.isAllItemsLoading()) return [];
    const capacity = Math.max(1, TOTAL_MENU_ROWS - this.topItemsRowsInView());
    const empty = capacity - this.filteredAllItems().length;
    const count = Math.min(capacity, Math.max(this.allItemsPageSize(), empty));
    return Array.from({ length: count }, (_, i) => i);
  });

  readonly showEmptyState = computed(
    () => !this.isAllItemsLoading() && this.filteredAllItems().length === 0,
  );

  readonly isSearching = computed(() => this.searchTerm().trim().length > 0);

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      if (this.previewTooltipSuppressionTimer !== undefined) {
        clearTimeout(this.previewTooltipSuppressionTimer);
      }
    });

    afterRenderEffect((onCleanup) => {
      // Re-run on any input change so the auto-fill loop keeps firing loadMore.
      this.allItems();
      this.hasMore();
      this.isAllItemsLoading();
      this.searchTerm();
      this.topItems();
      this.activeItemId();

      const container = this.scrollContainer()?.nativeElement;
      if (!container) return;

      const check = () => this.emitLoadMoreIfNeeded(container);

      container.addEventListener('scroll', check, { passive: true });
      onCleanup(() => container.removeEventListener('scroll', check));

      check();
    });
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    this.searchChange.emit(term);
  }

  onSearchClear(): void {
    this.searchTerm.set('');
    this.searchChange.emit('');
  }

  onItemClick(event: Event, item: BDMenuItem): void {
    if (item.disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.suppressPreviewTooltips();
    this.itemClick.emit(item);
    this.resetSearchTerm();
    this.dismiss.emit();
  }

  onFooterActionClick(event: Event, action: ActionButton): void {
    action.onClick(event);
    this.resetSearchTerm();
    this.dismiss.emit();
  }

  // Menu instance is long-lived (@if keeps it mounted across frame switches);
  // clear the local search on any commit so a reopen starts unfiltered.
  private resetSearchTerm(): void {
    if (!this.searchTerm()) return;
    this.searchTerm.set('');
    this.searchChange.emit('');
  }

  // PrimeNG's body-appended tooltip can re-anchor to a stale hover after the
  // popover closes; suppress previews briefly so it tears down first.
  private suppressPreviewTooltips(): void {
    this.previewTooltipsDisabled.set(true);
    if (this.previewTooltipSuppressionTimer !== undefined) {
      clearTimeout(this.previewTooltipSuppressionTimer);
    }
    this.previewTooltipSuppressionTimer = setTimeout(() => {
      this.previewTooltipsDisabled.set(false);
      this.previewTooltipSuppressionTimer = undefined;
    }, 300);
  }

  private emitLoadMoreIfNeeded(container: HTMLElement): void {
    if (
      container.clientHeight > 0 &&
      this.hasMore() &&
      !this.isAllItemsLoading() &&
      container.scrollHeight - container.scrollTop - container.clientHeight <
        SCROLL_LOAD_THRESHOLD_PX
    ) {
      this.loadMore.emit();
    }
  }
}
